import { useEffect, useState } from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import CheckBox from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import FormatBirth from "../../../helpers/formatBirth";
import { removeDots } from "../../../helpers/removeDots";
import {
  PROPOSTA_PLANO_ID_KEY,
  PROPOSTA_VENDEDOR_ID_KEY,
} from "../../../helpers/constants";
import { useAuth } from "../../../contexts/Auth";
import clienteApi from "../../../api/cliente.api";
import type { CreateCliente } from "../../../types/cliente";
import enqueueSnackbar from "../../../helpers/enqueueSnackbar";

function InputCadastro({ cpf }: { cpf: string }) {
  const { login } = useAuth();

  const [cadastroData, setCadastroData] = useState<CreateCliente>({
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    uf: "",
    cidade: "",
    bairro: "",
    nome: "",
    celular: "",
    email: "",
    dataNascimento: "",
    sexo: "",
    password: "",
    cpf: cpf,
  });

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [exibirSenha, setExibirSenha] = useState(false);

  const [dados, setDados] = useState({
    password: "",
    confirmPassword: "",
  });

  const [valido, setValido] = useState({
    tamanho: false,
    letrasMaiusculas: false,
    numeros: false,
  });

  const validarSenha = (valor: string) => {
    const tamanhoValido = valor?.length >= 8;
    const letrasMaiusculasValidas = (valor.match(/[A-Z]/g) || [])?.length >= 2;
    const numerosValidos = (valor.match(/[0-9]/g) || [])?.length >= 2;

    setValido({
      tamanho: tamanhoValido,
      letrasMaiusculas: letrasMaiusculasValidas,
      numeros: numerosValidos,
    });
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDados((prevDados) => ({ ...prevDados, password: event.target.value }));
    validarSenha(event.target.value);
  };

  const [sexoSelecionado, setSexoSelecionado] = useState(""); // Novo estado para o sexo selecionado

  const handleSexoChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSexoSelecionado(event.target.value);
    setCadastroData({
      ...cadastroData,
      sexo: event.target.value,
    });
  };

  const handleChange = (name: string, value: any) => {
    setCadastroData((prevDados) => ({
      ...prevDados,
      [name]: value,
    }));
  };

  function formatDateISO(inputDate: string): string {
    if (!inputDate) {
      return "";
    }

    const dateNumbers = inputDate.replace(/\D+/g, "");

    if (dateNumbers.length === 8) {
      return dateNumbers.replace(/(\d{2})(\d{2})(\d{4})/, "$3-$2-$1");
    }

    return dateNumbers;
  }

  function formatCelular(celular: string): string {
    if (!celular || celular === "") {
      return "";
    }

    const numbers = celular.replaceAll(/\D+/gi, "");

    if (numbers.length >= 10) {
      return numbers
        .substring(0, numbers[0] === "0" ? 12 : 11)
        .replace(/(0?\d{2})(\d{4,5})(\d{4})/, "($1) $2-$3");
    }

    return numbers;
  }

  function formatCEP(cep: string): string {
    if (!cep || cep === "") {
      return "";
    }

    const numbers = cep.replaceAll(/\D+/gi, "").substring(0, 8);

    if (numbers.length === 8) {
      return numbers.replace(/(\d{5})(\d{3})/, "$1-$2");
    }

    return numbers;
  }

  const sendLogin = async () => {
    try {
      setLoadingSubmit(true);

      const propostaPlanoId: string | null = localStorage.getItem(
        PROPOSTA_PLANO_ID_KEY
      );
      const franquiaVendedorId: string | null = localStorage.getItem(
        PROPOSTA_VENDEDOR_ID_KEY
      );

      const dadosSubmit = {
        ...cadastroData,
        dataNascimento: formatDateISO(cadastroData.dataNascimento),
        cpf: removeDots(cadastroData.cpf),
        celular: removeDots(cadastroData.celular),
        franquiaVendedorId: franquiaVendedorId
          ? Number(franquiaVendedorId)
          : null,
        propostaPlanoId: propostaPlanoId ? Number(propostaPlanoId) : null,
      };

      await clienteApi.create(dadosSubmit);

      await login(dadosSubmit.cpf, dadosSubmit.password);
    } catch (error) {
      enqueueSnackbar(
        error?.message || "Um erro inesperado aconteceu ao criar sua conta.",
        "error"
      );
    } finally {
      setLoadingSubmit(false);
    }
  };

  useEffect(() => {
    setCadastroData({ ...cadastroData, password: dados.password });
  }, [dados]);

  useEffect(() => {
    if (!!cadastroData.cep) {
      const cepNumbers = cadastroData.cep.replace(/\D+/, "");

      if (cepNumbers.length === 8) {
        axios
          .get(`https://viacep.com.br/ws/${cepNumbers}/json/`)
          .then((resp) => {
            const end = resp.data;

            setCadastroData({
              ...cadastroData,
              endereco: end.logradouro,
              uf: end.uf,
              cidade: end.localidade,
              bairro: end.bairro,
            });
          })
          .catch((error) => console.error(error));
      }
    }
  }, [cadastroData.cep]);

  return (
    <>
      <div style={styles.divHeaderContainer}>
        <span style={styles.textTitleHeader}>Informações pessoais</span>
      </div>

      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        <Grid item sm={12} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.nome}
            placeholder="Nome"
            onChange={(e) => handleChange("nome", e.target.value)}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <select
            style={{ ...styles.inputStyle, width: "100%" }}
            value={sexoSelecionado}
            onChange={handleSexoChange}
          >
            <option value="">Selecione</option>
            <option value="M">Masculino</option>
            <option value="F">Feminino</option>
          </select>
        </Grid>
        <Grid item sm={4} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={formatCelular(cadastroData.celular || "")}
            placeholder="Celular"
            onChange={(e) =>
              handleChange("celular", formatCelular(e.target.value))
            }
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={FormatBirth(cadastroData.dataNascimento || "")}
            placeholder="Data nascimento"
            onChange={(e) =>
              handleChange("dataNascimento", FormatBirth(e.target.value))
            }
          />
        </Grid>
        <Grid item sm={12} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.email}
            type="email"
            placeholder="E-mail"
            onChange={(e) => handleChange("email", e.target.value)}
          />
        </Grid>
      </Grid>

      <div style={styles.divHeaderContainer}>
        <span style={styles.textTitleHeader}>Informações de endereço</span>
      </div>
      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        <Grid item sm={8} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.cep}
            onChange={(e) => handleChange("cep", formatCEP(e.target.value))}
            placeholder="CEP"
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.uf}
            placeholder="UF"
            onChange={(e) => handleChange("uf", e.target.value)}
          />
        </Grid>
        <Grid item sm={8} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.endereco}
            placeholder="Rua"
            onChange={(e) => handleChange("endereco", e.target.value)}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.numero}
            placeholder="Número"
            onChange={(e) => handleChange("numero", e.target.value)}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.complemento}
            placeholder="Complemento"
            onChange={(e) => handleChange("complemento", e.target.value)}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.bairro}
            placeholder="Bairro"
            onChange={(e) => handleChange("bairro", e.target.value)}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={cadastroData.cidade}
            placeholder="Cidade"
            onChange={(e) => handleChange("cidade", e.target.value)}
          />
        </Grid>
      </Grid>

      <div style={styles.divHeaderContainer}>
        <span style={styles.textTitleHeader}>
          Senha para utilizar no aplicativo
        </span>
      </div>

      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        <Grid item sm={12} xs={12}>
          <input
            style={{ ...styles.inputStyle, width: "100%" }}
            value={dados.password}
            onChange={handlePasswordChange}
            placeholder="Senha"
            type={exibirSenha ? "text" : "password"}
          />
        </Grid>
      </Grid>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          style={styles.buttonExibirSenha}
          onClick={() => setExibirSenha(exibirSenha ? false : true)}
        >
          {exibirSenha ? "Esconder senha" : "Exibir senha"}
        </button>
      </div>
      <div style={{ marginLeft: 15, marginTop: 15 }}>
        <div style={styles.viewValidation}>
          {valido.tamanho ? (
            <CheckBox style={{ color: "#91D7A9" }} />
          ) : (
            <CheckBoxOutlineBlank style={{ color: "#191919" }} />
          )}
          <span style={styles.textValidation}>Mínimo 8 caracteres</span>
        </div>
        <div style={styles.viewValidation}>
          {valido.letrasMaiusculas ? (
            <CheckBox style={{ color: "#91D7A9" }} />
          ) : (
            <CheckBoxOutlineBlank style={{ color: "#191919" }} />
          )}
          <span style={styles.textValidation}>
            No mínimo 2 letras maiúsculas
          </span>
        </div>

        <div style={styles.viewValidation}>
          {valido.numeros ? (
            <CheckBox style={{ color: "#91D7A9" }} />
          ) : (
            <CheckBoxOutlineBlank style={{ color: "#191919" }} />
          )}
          <span style={styles.textValidation}>No mínimo 2 números</span>
        </div>
      </div>

      {loadingSubmit ? (
        <div style={{ padding: 25, marginBottom: 25 }}>
          Aguarde, estamos concluindo o cadastro <CircularProgress size={16} />
        </div>
      ) : (
        <button
          style={styles.buttonNext}
          onClick={() => sendLogin()}
          disabled={loadingSubmit}
        >
          Continuar
        </button>
      )}
    </>
  );
}

const styles = {
  inputStyle: {
    borderRadius: 18,
    padding: 16,
    marginTop: 15,
    border: "1px solid #bcbcbc",
    width: "70%",
  },
  buttonExibirSenha: {
    border: "none",
    background: "transparent",
    fontWeight: "600",
    textDecorationLine: "underline",
    cursor: "pointer",
    color: "#191919",
  },
  textTitleHeader: {
    fontWeight: "600",
    fontSize: 16,
    color: "#191919",
  },
  container: {
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#141414",
  },
  alertMessage: {
    color: "red",
    fontSize: 12,
    marginLeft: 5,
  },
  divHeaderContainer: {
    marginTop: 25,
    flexFlow: "column wrap",
    display: "flex",
  },
  footer: {
    width: "80%",

    borderTopColor: "#E0E0E0",
    borderTopWidth: 1,
  },
  textValidation: {
    fontSize: 12,
    paddingLeft: 10,
  },
  buttonNext: {
    backgroundColor: "#86BFC6",
    width: "70%",
    height: 43,
    borderRadius: 18,
    color: "#fff",
    border: "none",
    fontWeight: "600",
    fontSize: 14,
    cursor: "pointer",
    marginTop: 30,
  },
  viewValidation: {
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    fontSize: "16px",
  },
};

export default InputCadastro;
